import $apiJSON from '../http/apiJSON'

// Movie API Service

export async function getMovies(params = {}) {
  try {
    // Преобразуем параметры для соответствия API
    const apiParams = {}
    
    if (params.page) apiParams.page = params.page
    if (params.search) apiParams.search = params.search
    if (params.genre) apiParams.genre = params.genre
    if (params.year) apiParams.year = params.year
    if (params.rating) apiParams.min_rating = params.rating
    if (params.sort_by) {
      switch (params.sort_by) {
        case 'rating':
          apiParams.ordering = '-rating'
          break
        case 'year':
          apiParams.ordering = '-release_date'
          break
        case 'title':
          apiParams.ordering = 'title'
          break
        case 'popularity':
          apiParams.ordering = '-popularity'
          break
        default:
          apiParams.ordering = params.sort_by
      }
    }

    const response = await $apiJSON.get('movie/movies/', { params: apiParams })
    
    return {
      results: response.data.results || response.data,
      total_pages: response.data.total_pages || Math.ceil((response.data.count || 0) / 20),
      count: response.data.count || 0
    }
  } catch (error) {
    console.error('Error fetching movies:', error)
    throw error
  }
}

export async function getMovie(slug) {
  const response = await $apiJSON.get(`movie/movies/${slug}/`)
  return response.data
}

export async function getMovieDetails(movieId) {
  return getMovie(movieId)
}

export async function getMovieGenres() {
  return $apiJSON.get('movie/categories/')
}

export async function getGenres() {
  try {
    const response = await getMovieGenres()
    return response.data.results || response.data
  } catch (error) {
    console.error('Error fetching genres:', error)
    return []
  }
}

export async function getCategories() {
  return getGenres()
}

export async function getMovieEpisodes(slug, params = {}) {
  return $apiJSON.get(`movie/movies/${slug}/episodes/`, { params })
}

export async function updateWatchProgress(slug, episodeId = null, progress = 0) {
  return $apiJSON.post(`movie/movies/${slug}/watch_progress/`, { 
    episode_id: episodeId,
    progress_percent: progress 
  })
}

export async function getMovieDirectors(params = {}) {
  return $apiJSON.get('movie/directors/', { params })
}

export async function getMovieActors(params = {}) {
  return $apiJSON.get('movie/actors/', { params })
}

export async function rateMovie(slug, rating) {
  return $apiJSON.post(`movie/movies/${slug}/rate/`, { rating })
}

export async function getMovieRating(slug) {
  return $apiJSON.get(`movie/movies/${slug}/rating/`)
}

export async function addToWatchlist(slug, status = 'want_to_watch') {
  return $apiJSON.post(`movie/movies/${slug}/watchlist/`, { status })
}

export async function toggleLike(slug) {
  try {
    const response = await $apiJSON.post(`movie/movies/${slug}/like/`)
    return response.data
  } catch (error) {
    console.error('Error toggling like:', error)
    throw error
  }
}

export async function removeFromWatchlist(slug) {
  return $apiJSON.delete(`movie/movies/${slug}/watchlist/`)
}

export async function getWatchlist() {
  return $apiJSON.get('movie/watchlist/')
}

// Функции для избранного (алиасы для watchlist)
export async function getFavorites() {
  try {
    const response = await getWatchlist()
    return response.data.results || response.data
  } catch (error) {
    console.error('Error fetching favorites:', error)
    return []
  }
}

export async function addToFavorites(movieId) {
  return addToWatchlist(movieId, 'favorite')
}

export async function removeFromFavorites(movieId) {
  return removeFromWatchlist(movieId)
}

export async function getMovieReviews(slug) {
  try {
    const response = await $apiJSON.get(`movie/movies/${slug}/reviews/`)
    return {
      results: response.data.results || response.data,
      total_pages: response.data.total_pages || 1
    }
  } catch (error) {
    console.error('Error fetching movie reviews:', error)
    return { results: [], total_pages: 1 }
  }
}

export async function addMovieReview(slug, body, rating) {
  return $apiJSON.post(`movie/movies/${slug}/add-review/`, { body, rating })
}

export async function submitReview(movieId, body, rating) {
  return addMovieReview(movieId, body, rating)
}

export async function updateMovieReview(reviewId, data) {
  return $apiJSON.put(`movie/reviews/${reviewId}/`, data)
}

export async function deleteMovieReview(reviewId) {
  return $apiJSON.delete(`movie/reviews/${reviewId}/`)
}

export async function getMoviesByGenre(genreId, params = {}) {
  return $apiJSON.get('movie/movies/', { params: { genre: genreId, ...params } })
}

export async function getMoviesByDirector(directorId, params = {}) {
  return $apiJSON.get('movie/movies/', { params: { director: directorId, ...params } })
}

export async function getMoviesByActor(actorId, params = {}) {
  return $apiJSON.get('movie/movies/', { params: { actor: actorId, ...params } })
}

export async function searchMovies(query, page = 1) {
  return getMovies({ search: query, page })
}

export async function getPopularMovies(page = 1) {
  return getMovies({ page, sort_by: 'popularity' })
}

export async function getRecentMovies(page = 1) {
  return getMovies({ page, sort_by: 'year' })
}

export async function getSimilarMovies(movieId) {
  try {
    const response = await $apiJSON.get(`movie/movies/${movieId}/similar/`)
    return {
      results: response.data.results || response.data
    }
  } catch (error) {
    console.error('Error fetching similar movies:', error)
    return { results: [] }
  }
}

export async function getRecommendations() {
  try {
    const response = await $apiJSON.get('movie/recommendations/')
    return response.data.results || response.data
  } catch (error) {
    console.error('Error fetching recommendations:', error)
    return []
  }
}

export async function getMovieStats() {
  return $apiJSON.get('movie/stats/')
}

// Класс-обертка для удобства использования
class MovieService {
  static getMovies = getMovies
  static getMovie = getMovie
  static getMovieDetails = getMovieDetails
  static getGenres = getGenres
  static getCategories = getCategories
  static getFavorites = getFavorites
  static addToFavorites = addToFavorites
  static addToWatchlist = addToWatchlist
  static removeFromFavorites = removeFromFavorites
  static rateMovie = rateMovie
  static getMovieReviews = getMovieReviews
  static submitReview = submitReview
  static getSimilarMovies = getSimilarMovies
  static searchMovies = searchMovies
  static getRecommendations = getRecommendations
  static getMovieStats = getMovieStats
  static getPopularMovies = getPopularMovies
  static getRecentMovies = getRecentMovies
  static toggleLike = toggleLike
  static getMovieEpisodes = getMovieEpisodes
  static updateWatchProgress = updateWatchProgress
}

export default MovieService